import React from 'react';
import {
    Routes,
    Route,
    Navigate,
    useLocation,
} from 'react-router-dom';
import { Portal } from '@chakra-ui/portal';
import { ToastContainer } from 'react-toastify';

import Navbar from 'components/navbar';
import Sidebar from 'components/sidebar';
import Footer from 'components/footer/Footer';
import routes from 'routes';
import AIAssistant from 'components/ai/AIAssistant';
import { prefetchOfflineData } from 'services/prefetchOfflineData';
import { getMyInfo } from 'interfaces/user';

export default function Admin(props: { [x: string]: any }) {
    const { ...rest } = props;
    const location = useLocation();
    const [open, setOpen] = React.useState(true);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [fullSidebar, setFullSidebar] = React.useState(true);
    const [currentRoute, setCurrentRoute] = React.useState('');

    React.useEffect(() => {
        window.innerWidth < 1200 ? setOpen(false) : setOpen(true);
        //window.innerWidth < 1400 ? setFullSidebar(false) : setFullSidebar(true);
        window.addEventListener('resize', () => {
            window.innerWidth < 1200 ? setOpen(false) : setOpen(true);
            //window.innerWidth < 1400 ? setFullSidebar(false) : setFullSidebar(true);
        });
    }, []);

    React.useEffect(() => {
        if (localStorage.getItem('token') === null) {
            window.location.href = '/auth/login';
        }
        getActiveRoute(routes);
        // eslint-disable-next-line
    }, [location.pathname]);

    // Pre-warm the offline cache once after login, in the background
    React.useEffect(() => {
        prefetchOfflineData();
        // eslint-disable-next-line
    }, []);

    // Cache pilotageOkr from user info so the sidebar can filter the OKR route.
    // Also identifies the logged-in user in the main app's Crisp widget (email/company/name),
    // so support sees who they're talking to. Not needed on the BtoB shop: that chat already
    // identifies customers via CrispChatLoader (utils/crisp.tsx).
    // Uses sessionStorage as a cache: the API is called at most once per browser session,
    // not on every page navigation (which remounts the admin layout).
    React.useEffect(() => {
        if (!localStorage.getItem('token')) return;

        const identifyCrisp = (email?: string, company?: string, fullName?: string) => {
            if (!window.$crisp) return;
            if (email) window.$crisp.push(['set', 'user:email', [email]]);
            if (fullName) window.$crisp.push(['set', 'user:nickname', [fullName]]);
            if (company) window.$crisp.push(['set', 'user:company', [company]]);
        };

        const cached = sessionStorage.getItem('pilotageOkrCached');
        if (cached !== null) {
            // Already fetched this session — apply cached value and skip the API call
            localStorage.setItem('pilotageOkr', cached);
            window.dispatchEvent(new Event('toola-features-loaded'));
            identifyCrisp(
                sessionStorage.getItem('crispEmail') || undefined,
                sessionStorage.getItem('crispCompany') || undefined,
                sessionStorage.getItem('crispName') || undefined,
            );
            return;
        }

        getMyInfo().then((info) => {
            if (info?.data) {
                const value = info.data.pilotageOkr === true ? 'true' : 'false';
                localStorage.setItem('pilotageOkr', value);
                sessionStorage.setItem('pilotageOkrCached', value); // cache for this session
                window.dispatchEvent(new Event('toola-features-loaded'));

                const { email, company, fullName } = info.data;
                if (email) sessionStorage.setItem('crispEmail', email);
                if (company) sessionStorage.setItem('crispCompany', company);
                if (fullName) sessionStorage.setItem('crispName', fullName);
                identifyCrisp(email, company, fullName);
            }
        });
        // eslint-disable-next-line
    }, []);

    // functions for changing the states from components
    const getActiveRoute = (routes: RoutesType[]): string => {
        let activeRoute = 'Default Brand Text';
        for (let i = 0; i < routes.length; i++) {
            if (routes[i].collapse) {
                let collapseActiveRoute = getActiveRoute(routes[i].items);
                if (collapseActiveRoute !== activeRoute) {
                    return collapseActiveRoute;
                }
            } else {
                if (
                    window.location.href.indexOf(
                        routes[i].layout + routes[i].path,
                    ) !== -1
                ) {
                    setCurrentRoute(routes[i].name);
                }
            }
        }
        return activeRoute;
    };
    const getActiveNavbar = (routes: RoutesType[]): boolean => {
        let activeNavbar = false;
        for (let i = 0; i < routes.length; i++) {
            if (routes[i].collapse) {
                let collapseActiveNavbar = getActiveNavbar(routes[i].items);
                if (collapseActiveNavbar !== activeNavbar) {
                    return collapseActiveNavbar;
                }
            } else {
                if (
                    window.location.href.indexOf(
                        routes[i].layout + routes[i].path,
                    ) !== -1
                ) {
                    return routes[i].secondary;
                }
            }
        }
        return activeNavbar;
    };
    const getRoutes = (routes: RoutesType[]): any => {
        return routes.map((prop, key) => {
            if (prop.layout === '/admin' || prop.layout === '') {
                return (
                    <Route
                        path={`${prop.path}`}
                        element={prop.component}
                        key={key}
                    />
                );
            }
            if (prop.collapse) {
                return getRoutes(prop.items);
            }
            return null;
        });
    };
    document.documentElement.dir = 'ltr';
    return (
        <div className="flex h-full w-full">
            <Sidebar
                open={open}
                fullSidebar={fullSidebar}
                onClose={() => setOpen(false)}
            />
            {/* Navbar & Main Content */}
            <div className="h-full w-full font-dm dark:bg-navy-900">
                {/* Main Content */}
                <main
                    className={`mx-2.5 flex-none transition-all dark:bg-navy-900 md:pr-2 ${
                        fullSidebar ? 'xl:ml-[313px]' : 'xl:ml-[160px]'
                    }`}
                >
                    <ToastContainer />
                    {/* Routes */}
                    <div>
                        <Portal>
                            <Navbar
                                onOpenSidenav={() => setOpen(!open)}
                                openSideNav={() => {
                                    if (window.innerWidth < 1200)
                                        setOpen(!open);
                                    else setOpen(true);
                                }}
                                brandText={currentRoute}
                                secondary={getActiveNavbar(routes)}
                                {...rest}
                            />
                        </Portal>
                        <div
                            className="mx-auto min-h-screen p-2 !pt-[55px] sm:!pt-[75px] md:p-2"
                            onClick={() => {
                                if (window.innerWidth < 1200) setOpen(false);
                            }}
                        >
                            <Routes>
                                {getRoutes(routes)}
                                <Route
                                    path="/"
                                    element={
                                        <Navigate to="/dashboard" replace />
                                    }
                                />
                            </Routes>
                        </div>
                        <div className="p-3">
                            <Footer />
                        </div>
                    </div>
                </main>
            </div>
            <AIAssistant />
        </div>
    );
}
